import { Layer } from '@deck.gl/core/typed';
import { GeoJsonLayer, GeoJsonLayerProps } from '@deck.gl/layers/typed';
import {
  Box,
  ButtonGroup,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  styled,
  Tooltip,
} from '@mui/material';
import {
  centroid,
  Feature,
  FeatureCollection,
  featureCollection,
  MultiPolygon,
  Point,
  Polygon,
} from '@turf/turf';

import { useLogEvent } from 'hooks/useLogEvent';
import { ThemeButton, ThemeTypography } from 'DesignSystem';
import JsonEditor from 'DesignSystem/Inputs/JsonEditor/JsonEditor';
import HorizontalScrollContainer from 'DesignSystem/Layout/HorizontalScrollContainer/HorizontalScrollContainer';
import CustomMap, { ICustomMapRef, IMapPopup } from 'DesignSystem/Map/CustomMap';
import { calculateArea } from 'DesignSystem/Map/utils/map.utils';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import omitDeep from 'omit-deep-lodash';
import { MapEvent } from 'react-map-gl';
import React, { ChangeEvent, FC, Ref, useEffect, useMemo, useState } from 'react';
import useMeasure from 'react-use/lib/useMeasure';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { ICoordinates } from 'types/map.types';
import copyTextToClipboard from 'utils/clipboard.utils';
import { formatAndRoundNumber } from 'utils/number.utils';
import { v4 as uuid } from 'uuid';
import FlexBox from 'Components/FlexBox';

export type IGeoDataProps = {
  marker?: ICoordinates;
  defaultView?: 'map' | 'json';
  /**
   * In case the background color should be light for the items (white)
   */
  lightBackground?: boolean;
  /**
   * The default features to be shown on the map when the component is mounted
   * This can be neighbouring plots or any other features
   */
  geoJsonLayerProps?: GeoJsonLayerProps;
  /**
   * Name of the geoJson layer which can be enabled or disabled
   */
  geoJsonLayerLabel?: string;
  geoJsonLayerLoading?: boolean;
  defaultCenter?: ICoordinates;
  infoPopup?: IMapPopup;
  /** Reference to the component itself to call function inside of it */
  customRef?: Ref<ICustomMapRef>;
  viewOnly?: boolean;
  /**
   * The zoom level of the map when the component is mounted, it will try to center the map on the marker if provided
   * @default 2
   */
  defaultZoomLevel?: number;
  onMarkerPopupClose?: () => void;
  /**
   * This button is shown when in view only mode to allow adding new plots via a different interface
   */
  onAddNewPlotClick: () => void;
} & (
  | {
      value?: FeatureCollection<Polygon | Point | MultiPolygon>;
      /**
       * Defines if the component is showing a feature collection or a single feature (point or polygon)
       * 'feature' will only allow adding a single point or polygon
       * @default 'feature-collection'
       */
      type?: 'feature-collection';
      /**
       * @param value FeatureCollection<Polygon | Point | MultiPolygon>
       * @param areaSizeHa The calculated area size in hectares of all polygon features combined. Point will be ignored in the calculation.
       * @returns
       */
      onChange: (
        value: FeatureCollection<Polygon | Point | MultiPolygon>,
        areaSizeHa: number
      ) => void;
    }
  | {
      value?: Feature<Polygon | Point | MultiPolygon> | null;
      type: 'feature';
      onChange: (value: Feature<Polygon | Point | MultiPolygon> | null, areaSizeHa: number) => void;
    }
);

const POLYGON_FEATURE_PLACEHOLDER = {
  type: 'Feature',
  properties: {
    plotId: 'abc-111',
  },
  geometry: {
    type: 'Polygon',
    coordinates: [
      [
        [25.82545, 14.0557864],
        [24.7326, 11.838458],
        [27.0359, 11.33425],
        [29.7282, 13.43176],
        [25.82545, 14.0557],
      ],
    ],
  },
};

const POINT_FEATURE_PLACEHOLDER = {
  type: 'Feature',
  properties: {
    plotId: 'abc-222',
  },
  geometry: {
    type: 'Point',
    coordinates: [19.48262, 14.43176],
  },
};

const FEATURE_COLLECTION_PLACEHOLDER = {
  type: 'FeatureCollection',
  features: [POINT_FEATURE_PLACEHOLDER, POLYGON_FEATURE_PLACEHOLDER],
};

const EMPTY_FEATURE_COLLECTION: FeatureCollection<MultiPolygon | Polygon | Point> = {
  type: 'FeatureCollection',
  features: [],
};

const CultivatedAreasSize = styled(Box)<{ 'map-spacing': Tbooleanish }>(
  ({ theme, 'map-spacing': mapSpacing }) => ({
    position: 'absolute',
    background: theme.custom.themeColors.grayScale[20],
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    zIndex: 3,
    right: theme.spacing(mapSpacing === 'true' ? 6 : 4),
    top: mapSpacing === 'true' ? 66 : theme.spacing(5),
  })
);

const Container = styled('div')(() => ({
  position: 'relative',
  height: '100%',
  overflow: 'hidden',
}));

const Coordinate = styled('div')<{ 'light-background'?: Tbooleanish; disabled?: Tbooleanish }>(
  ({ theme, 'light-background': lightBackground, disabled }) => ({
    cursor: disabled === 'true' ? 'default' : 'copy',
    padding: '4px 8px',
    borderRadius: '4px',
    background:
      lightBackground === 'true'
        ? theme.custom.themeColors.white
        : theme.custom.themeColors.grayScale[10],
  })
);

const CustomGeoJsonEditor: FC<{
  type: 'feature-collection' | 'feature';
  viewOnly?: boolean;
  height: number | string;
  geoJsonCollection: FeatureCollection<Polygon | Point | MultiPolygon>;
  onChange: (value: FeatureCollection<Polygon | Point | MultiPolygon> | null) => void;
}> = ({ type, viewOnly, height, geoJsonCollection, onChange }) => {
  /**
   * Sanitize the feature collection to remove any id properties and __typename properties
   */
  const value = useMemo(() => {
    return featureCollection(
      geoJsonCollection.features.map(feature => {
        const { id, ...rest } = {
          id: undefined,
          ...omitDeep(feature, '__typename'),
        } as Feature<Polygon | Point | MultiPolygon>;
        return rest;
      })
    );
  }, [geoJsonCollection]);

  if (type === 'feature') {
    return (
      <JsonEditor<Feature<Polygon | Point | MultiPolygon>>
        placeholder={POLYGON_FEATURE_PLACEHOLDER}
        viewOnly={viewOnly}
        value={value.features[0]}
        validationFormat="geojson-feature-point-polygon"
        errorMessage="Invalid GeoJSON format. Please use the RFC 7946 standard. Allowed feature types are point and polygon."
        hintMessage="This is an example to illustrate the GeoJson data format."
        fileProperties={{
          name: 'farm-plot',
          type: 'application/geo+json',
          extension: 'geojson',
        }}
        minHeight={height}
        onChange={value => onChange(featureCollection(value ? [value] : []))}
      />
    );
  }

  return (
    <JsonEditor<FeatureCollection<Polygon | Point | MultiPolygon>>
      placeholder={FEATURE_COLLECTION_PLACEHOLDER}
      viewOnly={viewOnly}
      value={value}
      validationFormat="geojson-feature-collection-point-polygon"
      errorMessage="Invalid GeoJSON format. Please use the RFC 7946 standard. Allowed feature collection types are point and polygon."
      hintMessage="This is an example to illustrate the GeoJson data format."
      fileProperties={{
        name: 'farm-plots',
        type: 'application/geo+json',
        extension: 'geojson',
      }}
      minHeight={height}
      onChange={onChange}
    />
  );
};

const GeoData: FC<IGeoDataProps> = ({
  type = 'feature-collection',
  value,
  defaultCenter,
  defaultView = 'map',
  geoJsonLayerProps,
  geoJsonLayerLabel,
  geoJsonLayerLoading,
  defaultZoomLevel = 2,
  lightBackground,
  marker,
  infoPopup,
  viewOnly,
  customRef,
  onChange,
  onMarkerPopupClose,
  onAddNewPlotClick,
}) => {
  const { logEvent } = useLogEvent();
  const [ref, size] = useMeasure<HTMLDivElement>();

  const [view, setView] = useState<'map' | 'json'>(defaultView);
  const [mapRef, setMapRef] = useState<MapEvent['target'] | null>(null);
  const [copiedCoordinatesForIndex, setCoordinatesCopiedForIndex] = useState<number | undefined>();
  const [showGeoJsonLayer, setShowGeoJsonLayer] = useState<boolean>(true);

  /**
   * We need to unmount the layers every time the view changes and build a new one
   * This is because the mapbox layers are not reactive and do not update when the view changes
   * https://github.com/visgl/deck.gl/discussions/5638
   */
  const [geoJsonLayer, setGeoJsonLayer] = useState<Layer | undefined>(undefined);

  /**
   * Features drawn on map that are always in LngLat format from MapBox
   */
  const [displayedFeatureCollection, setDisplayedFeatureCollection] = useState<
    FeatureCollection<Polygon | Point | MultiPolygon>
  >(
    value
      ? type === 'feature-collection'
        ? (value as FeatureCollection<Polygon | Point | MultiPolygon>)
        : featureCollection([value as Feature<Polygon | Point | MultiPolygon>])
      : EMPTY_FEATURE_COLLECTION
  );

  /**
   * Allow drawing only one feature when in 'feature' mode
   */
  const hideDrawingButtons =
    viewOnly || (type === 'feature' && displayedFeatureCollection.features.length >= 1);

  const currentPolygonSizeHa = useMemo(
    () => calculateArea(displayedFeatureCollection),
    [displayedFeatureCollection]
  );

  const plotCenterCoordinates = useMemo(() => {
    // The centroid function can sometimes fail, for example if the polygon is invalid with 2 points only
    try {
      return displayedFeatureCollection?.features.length
        ? displayedFeatureCollection?.features.map(feature => centroid(feature))
        : undefined;
    } catch {
      return undefined;
    }
  }, [displayedFeatureCollection]);

  /** Update default if controlled input */
  useEffect(() => {
    if (!value) {
      setDisplayedFeatureCollection(EMPTY_FEATURE_COLLECTION);
      return;
    }

    if (type === 'feature-collection') {
      setDisplayedFeatureCollection(value as FeatureCollection<Polygon | Point | MultiPolygon>);
    } else {
      setDisplayedFeatureCollection(
        featureCollection([value as Feature<Polygon | Point | MultiPolygon>])
      );
    }
  }, [value, type]);

  /** Update default if controlled input */
  useEffect(() => {
    if (view === 'map' && geoJsonLayerProps && showGeoJsonLayer) {
      setGeoJsonLayer(new GeoJsonLayer(geoJsonLayerProps));
    } else {
      setGeoJsonLayer(undefined);
    }
  }, [view, showGeoJsonLayer, geoJsonLayerProps]);

  useEffect(() => {
    if (type === 'feature') {
      (
        onChange as (
          value: Feature<Polygon | Point | MultiPolygon> | null,
          areaSizeHa: number
        ) => void
      )(displayedFeatureCollection.features[0] || null, currentPolygonSizeHa);
    } else if (type === 'feature-collection') {
      (
        onChange as (
          value: FeatureCollection<Polygon | Point | MultiPolygon>,
          areaSizeHa: number
        ) => void
      )(displayedFeatureCollection, currentPolygonSizeHa);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFeatureCollection, currentPolygonSizeHa]);

  /**
   * Update the map position when the location field changes
   */
  useEffect(() => {
    // Only update the map if it is shown and there are no features yet
    if (view === 'map' && !displayedFeatureCollection.features?.length && marker && mapRef) {
      mapRef.flyTo({
        center: [marker.lng, marker.lat],
        zoom: 14,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker, mapRef]);

  const handleDrawCreate = (newFeature: Feature<Point | Polygon | MultiPolygon> | undefined) => {
    if (newFeature) {
      logEvent('DRAW_CREATE_POLYGON');
      setDisplayedFeatureCollection(prev => featureCollection([...prev.features, newFeature]));
    }
  };

  const handleDrawUpdate = (
    updatedFeature: Feature<Point | Polygon | MultiPolygon> | undefined
  ) => {
    if (updatedFeature) {
      logEvent('DRAW_UPDATE_POLYGON');
      setDisplayedFeatureCollection(prev =>
        featureCollection([
          ...prev.features.filter(({ id }) => id !== updatedFeature.id), // Remove old feature
          updatedFeature,
        ])
      );
    }
  };

  const handleDrawDelete = (deleteFeature: Feature<Point | Polygon | MultiPolygon> | undefined) => {
    if (deleteFeature) {
      logEvent('DRAW_DELETE_POLYGON');
      setDisplayedFeatureCollection(prev =>
        featureCollection(prev.features.filter(({ id }) => id !== deleteFeature.id))
      );
    }
  };

  const handleJsonChange = (value: FeatureCollection<Point | Polygon | MultiPolygon> | null) => {
    logEvent('ENTERED_GEO_JSON_DATA');
    if (!value) {
      setDisplayedFeatureCollection(EMPTY_FEATURE_COLLECTION);
      return;
    }
    const features = value.features;
    // We have to make sure each feature has a unique id for the drawing feature to work correctly
    setDisplayedFeatureCollection(
      featureCollection(
        features.map(feature =>
          !feature.id || features.filter(({ id }) => id === feature.id).length > 1
            ? { ...feature, id: uuid() }
            : feature
        )
      )
    );
  };

  const handleGeoJsonLayerToggle = (event: ChangeEvent<HTMLInputElement>) => {
    setShowGeoJsonLayer(event.target.checked);
  };

  return (
    <Box width="100%" height="100%" overflow="hidden">
      <FlexBox gap={2}>
        <ButtonGroup>
          <ThemeButton
            className={view === 'map' ? 'selected' : ''}
            onClick={() => setView('map')}
            typographyColor="ALMOST_BLACK"
          >
            Drawing tool
          </ThemeButton>

          <ThemeButton
            className={view === 'json' ? 'selected' : ''}
            onClick={() => setView('json')}
            typographyColor="ALMOST_BLACK"
          >
            Raw data
          </ThemeButton>
        </ButtonGroup>

        {viewOnly && onAddNewPlotClick ? (
          <ThemeButton
            color="BLUE_ICE"
            startIcon={<Icon name="plus" />}
            onClick={onAddNewPlotClick}
          >
            Add plot
          </ThemeButton>
        ) : (
          <>
            <Icon name="arrow-line-left" color="gray-40" />
            <ThemeTypography variant="BODY_MEDIUM" color="GRAY_40">
              Already have the plot coordinates? Add GeoJson raw data here.
            </ThemeTypography>
          </>
        )}
      </FlexBox>

      <FlexBox gap={0.5}>
        <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_60">
          Plot center coordinates (lng, lat):
        </ThemeTypography>
        <HorizontalScrollContainer>
          {!plotCenterCoordinates?.length ? (
            <Coordinate light-background={Booleanish(lightBackground)} disabled="true">
              <ThemeTypography variant="BODY_MEDIUM" color="GRAY_60">
                -
              </ThemeTypography>
            </Coordinate>
          ) : (
            plotCenterCoordinates.map(({ geometry: { coordinates }, id }, index) => (
              <Tooltip
                key={`coordinates-${id}-${index}`}
                leaveDelay={400}
                open={copiedCoordinatesForIndex === index}
                title="Copied center coordinates to clipboard"
                onClose={() => setCoordinatesCopiedForIndex(undefined)}
              >
                <Coordinate
                  light-background={Booleanish(lightBackground)}
                  onClick={event => {
                    event.stopPropagation();
                    setCoordinatesCopiedForIndex(index);
                    copyTextToClipboard(coordinates.join(', ') || '');
                  }}
                >
                  <ThemeTypography variant="BODY_MEDIUM" color="GRAY_60" autoOverflow>
                    {`${coordinates[0].toFixed(6)}, ${coordinates[1].toFixed(6)}`}
                  </ThemeTypography>
                </Coordinate>
              </Tooltip>
            ))
          )}
        </HorizontalScrollContainer>
      </FlexBox>

      <Container>
        <CultivatedAreasSize map-spacing={Booleanish(view === 'map')}>
          <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
            Total area
          </ThemeTypography>
          <ThemeTypography variant="BODY_SMALL">
            {formatAndRoundNumber(currentPolygonSizeHa, 2)} ha
          </ThemeTypography>
        </CultivatedAreasSize>

        {view === 'json' ? (
          <Box width="100%" height="calc(100% - 60px)" position="relative">
            <FlexBox mb={1} justifyContent="space-between">
              <ThemeTypography variant="BODY_MEDIUM">
                Paste and manage the GeoJson data in text field below.
              </ThemeTypography>
            </FlexBox>
            <Box ref={ref} maxHeight="calc(100% - 90px)">
              <CustomGeoJsonEditor
                type={type}
                geoJsonCollection={displayedFeatureCollection}
                viewOnly={viewOnly}
                height={size.height}
                onChange={handleJsonChange}
              />
            </Box>
          </Box>
        ) : (
          <Box width="100%" height="calc(100% - 60px)" position="relative">
            <Box mb={1}>
              <ThemeTypography variant="BODY_MEDIUM">
                {viewOnly
                  ? 'Edit all plots below to mark the cultivation area of this farm'
                  : 'Draw polygon shapes below to mark the cultivation area of this farm.'}
              </ThemeTypography>
            </Box>

            {!geoJsonLayerProps && geoJsonLayerLoading && (
              <FlexBox mt={-0.5} mb={2} position="relative" gap={1}>
                <CircularProgress size={12} />
                <ThemeTypography sx={{ userSelect: 'none' }} variant="BODY_MEDIUM">
                  {geoJsonLayerLabel}
                </ThemeTypography>
              </FlexBox>
            )}

            {geoJsonLayerProps && (
              <Box mt={-1} mb={0.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showGeoJsonLayer}
                      onChange={handleGeoJsonLayerToggle}
                    />
                  }
                  label={
                    <ThemeTypography sx={{ userSelect: 'none' }} variant="BODY_MEDIUM">
                      {geoJsonLayerLabel}
                    </ThemeTypography>
                  }
                />
              </Box>
            )}

            <Box height="calc(100% - 80px)">
              <CustomMap
                customRef={customRef}
                layers={[geoJsonLayer]}
                infoPopup={infoPopup}
                defaultDrawFeatures={displayedFeatureCollection}
                mapStyle="satellite"
                initialViewState={
                  defaultCenter
                    ? {
                        latitude: defaultCenter?.lat,
                        longitude: defaultCenter?.lng,
                        zoom: defaultZoomLevel,
                      }
                    : undefined
                }
                markers={
                  marker
                    ? [
                        {
                          id: 'farm-location',
                          coordinate: [marker?.lng, marker?.lat],
                          tooltip: 'Location center of the farm',
                        },
                      ]
                    : undefined
                }
                config={{
                  enableDrawing: true,
                  enableMapStyleToggle: true,
                  enableCenterButton: true,
                  hideDrawingButtons,
                  centerButtonText: type === 'feature' ? 'Center plot' : 'Center plot(s)',
                }}
                markerOptions={{
                  customIcon: 'site-location-point',
                  color: 'light-violet',
                }}
                onDrawCreate={handleDrawCreate}
                onDrawUpdate={handleDrawUpdate}
                onDrawDelete={handleDrawDelete}
                onMarkerPopupClose={onMarkerPopupClose}
                onMapLoad={setMapRef}
              />
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default GeoData;
